import { useState } from 'react';
import { allergenLabel } from '@verlo/shared/lib/allergens';
import { Button } from '@verlo/shared/components/ui/button';
import { Badge } from '@verlo/shared/components/ui/badge';
import {
  Plus,
  Users,
  Clock,
  User,
  Calendar,
  Eraser,
  ArrowLeftRight,
  MoreHorizontal,
  Cake,
} from 'lucide-react';
import BackBar from '../BackBar';
import { turnStatus } from '../../lib/fohState';
import {
  Select, SelectContent, SelectItem, SelectTrigger,
} from '@verlo/shared/components/ui/select';
import { Signal } from '@verlo/shared/components/ui/signal';
import { occasionWords, occasionIcon } from '../tables/OccasionPicker';
import { duration } from '@verlo/shared/lib/duration';

// The dining stages, in order. The kitchen's tickets advance a table through them on their
// own (backend kitchen.py); the picker is for the server to say so first — "awaiting
// desserts" before the dessert ticket fires — and it holds until the kitchen passes it.
export const STAGES = [
  { value: 'seated', label: 'Seated', tone: null },
  { value: 'ordering', label: 'Ordering', tone: null },
  { value: 'awaiting_starters', label: 'Awaiting starters', tone: 'warn' },
  { value: 'starters_served', label: 'Starters served', tone: 'warn' },
  { value: 'awaiting_mains', label: 'Awaiting mains', tone: 'warn' },
  { value: 'mains_served', label: 'Mains served', tone: 'warn' },
  { value: 'awaiting_desserts', label: 'Awaiting desserts', tone: 'warn' },
  { value: 'desserts_served', label: 'Desserts served', tone: 'warn' },
  { value: 'awaiting_bill', label: 'Awaiting bill', tone: 'stop' },
  { value: 'bill_paid', label: 'Bill paid', tone: 'ok' },
];

// The order page's header. One 48px row (4:3 tills, Oct 2026): back, the table, its guests,
// the time left, the occasion and the allergy. The allergy is never folded away, and gets the
// room the rest leave. The occasion sits in the row too, and a tap on it changes it; with
// none set, "Add an occasion" is behind ⋯, as "it's her birthday" often comes after the
// menus. What a server reaches for less often (the stage, which the kitchen moves on its own,
// Move, Clear table) and what's only worth a glance (the guest's name, the table's later
// bookings) sits behind ⋯. Running over shows on the time itself, in red.
//
// It used to be the back bar plus a card of chips and bookings, about 270px tall, which left
// the menu around 100px on a 1024x768 till before it scrolled.

const Chip = ({ onClick, testId, tone, children, className = '' }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={!onClick}
    className={`relative inline-flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap px-2.5 text-[13px] font-medium transition-colors after:absolute after:inset-x-0 after:-inset-y-1 after:content-[''] ${
      tone === 'stop' ? 'bg-signal-stop-tint text-signal-stop' : 'bg-surface-2 text-ink-soft hover:bg-paper'} ${className}`}
    data-testid={testId}
  >
    {children}
  </button>
);

const TableOrderHeader = ({
  table,
  timeInfo,
  upcomingBookings = [],
  onBack,
  onStatusChange,
  onStageChange,
  onEditCovers,
  onEditTime,
  onEditAllergy,
  onEditOccasion,
  onMove,
  from,
  title,
  subtitle,
}) => {
  const [moreOpen, setMoreOpen] = useState(false);
  const turn = turnStatus(table || {});
  const occupied = table?.status === 'occupied';
  const stage = STAGES.find((x) => x.value === (table?.dining_stage || 'seated')) || STAGES[0];
  // The allergens are short and always shown in full; only the free-text note gives way, and
  // a tap opens it whole (M5, Oct 2026: a till has no hover to read a cut-off title).
  const allergenText = (table?.allergens || []).map(allergenLabel).join(', ');
  const allergyNote = table?.has_allergies ? (table?.allergy_notes || '').trim() : '';
  const allergyText = [allergenText, allergyNote].filter(Boolean).join(' · ');
  const occasion = occasionWords(table);
  const OccasionIcon = occasionIcon(table?.occasion);
  const guest = table?.reservation?.guest_name;
  // Something worth a look behind ⋯: a dot on it says so.
  const moreHasNews = Boolean(guest || upcomingBookings.length);
  const hasMore = occupied || moreHasNews;

  return (
    <BackBar
      compact
      fallback={from}
      title={title || table?.name || 'New order'}
      subtitle={subtitle || (!occupied ? (table?.status === 'reserved' ? 'reserved' : table?.status || undefined) : undefined)}
      onBack={onBack}
    >
      {table?.covers > 0 && (
        <Chip onClick={onEditCovers} testId="covers-badge">
          <Users size={15} />{table.covers}<span className="sr-only"> {table.covers === 1 ? 'guest' : 'guests'}</span>
        </Chip>
      )}
      {occupied && timeInfo && (
        <Chip onClick={onEditTime} testId="time-badge" tone={timeInfo.isOvertime ? 'stop' : null}>
          <Clock size={15} />{timeInfo.text}
        </Chip>
      )}
      {occasion && (
        <Chip onClick={onEditOccasion} testId="occasion-badge" className="min-w-0 max-w-[11rem]">
          <OccasionIcon size={15} className="shrink-0" /><span className="truncate">{occasion}</span>
        </Chip>
      )}
      {/* The allergy: never hidden, the one thing in the row that gives way to the others. */}
      {allergyText ? (
        <button
          type="button"
          onClick={onEditAllergy}
          disabled={!onEditAllergy}
          className="flex min-h-9 min-w-0 flex-1 items-center gap-2 border-l-[3px] border-l-signal-stop-line bg-white px-2.5 py-0.5 text-left"
          title={allergyText}
          data-testid="allergy-badge"
        >
          <span className="shrink-0 font-heading text-[10.5px] font-bold uppercase tracking-[0.06em] text-signal-stop">Allergy</span>
          {/* The allergens on the first line, whole; the note under them, cut short if it must
              be, so it's always seen to be there. */}
          <span className="flex min-w-0 flex-col">
            {allergenText && <span className="text-[13px] font-semibold leading-tight text-ink" data-testid="allergy-badge-allergens">{allergenText}</span>}
            {allergyNote && (
              <span className={`truncate leading-tight ${allergenText ? 'text-[11.5px] text-ink-soft' : 'text-[13px] text-ink'}`} data-testid="allergy-badge-note">
                {allergyNote}
              </span>
            )}
          </span>
        </button>
      ) : (
        <>
          {occupied && onEditAllergy && (
            <Chip onClick={onEditAllergy} testId="add-allergy"><Plus size={14} />Allergy</Chip>
          )}
          <span className="flex-1" />
        </>
      )}
      {hasMore && (
        <div className="relative shrink-0">
          <Button variant="outline" size="icon" className="relative h-9 w-9 after:absolute after:-inset-1 after:content-['']" onClick={() => setMoreOpen((v) => !v)}
                  aria-expanded={moreOpen} title="More about this table" data-testid="table-more-btn">
            <MoreHorizontal size={16} />
            {moreHasNews && <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-verlo" aria-hidden />}
          </Button>
          {moreOpen && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setMoreOpen(false)} aria-hidden />
              <div className="absolute right-0 top-full z-40 mt-1 w-72 space-y-1 border border-hairline bg-white p-1 shadow-lg" data-testid="table-more-menu">
                {occupied && (
                  <div className="flex items-center justify-between gap-2 px-3 py-2">
                    <span className="text-[13px] text-ink-soft">Stage</span>
                    <Select value={stage.value} onValueChange={onStageChange}>
                      <SelectTrigger className="h-10 w-auto gap-1.5 border-hairline bg-white px-2.5" data-testid="dining-stage-selector">
                        <Signal tone={stage.tone || 'info'} dot={Boolean(stage.tone)}>{stage.label}</Signal>
                      </SelectTrigger>
                      <SelectContent className="z-50">
                        {STAGES.map((x) => (
                          <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {/* Over its time: a row of its own, so the Stage box stays the width of the stage
                    (Oct 2026), and the way to give the table longer is right beside it. */}
                {occupied && turn?.over && (
                  <div className="flex items-center justify-between gap-2 px-3 pb-2" data-testid="table-over-time">
                    <Signal tone="warn">{duration(turn.minutes)} over</Signal>
                    {onEditTime && (
                      <button type="button" className="h-9 px-2 text-[13px] font-medium text-verlo hover:underline"
                              onClick={() => { setMoreOpen(false); onEditTime(); }} data-testid="over-time-add">
                        Add time
                      </button>
                    )}
                  </div>
                )}
                {guest && (
                  <div className="space-y-1.5 border-t border-hairline px-3 py-2">
                    <div className="flex items-center gap-2 text-[13px]">
                      <User size={14} className="text-ink-soft" />
                      <span className="font-medium text-ink">{guest}</span>
                      {table.reservation.is_frequent && <span className="text-[12px] text-ink-soft">· Frequent</span>}
                    </div>
                    {/* Guest email intentionally not shown: this is a shared screen. */}
                  </div>
                )}
                {upcomingBookings.length > 0 && (
                  <div className="space-y-1.5 border-t border-hairline px-3 py-2" data-testid="table-upcoming-bookings">
                    <div className="flex items-center gap-2 text-[12px] font-medium text-ink-soft">
                      <Calendar size={13} /> Later at {table?.name}
                    </div>
                    {upcomingBookings.map((booking) => (
                      <div key={booking.id} className="flex items-center gap-2 text-[13px]">
                        <Badge className="bg-verlo text-xs">{booking.time}</Badge>
                        <span className="truncate font-medium text-ink-soft">{booking.customer_name}</span>
                        <span className="ml-auto flex items-center gap-1 text-[12px] text-ink-soft"><Users size={11} /> {booking.party_size}</span>
                      </div>
                    ))}
                  </div>
                )}
                {occupied && !occasion && onEditOccasion && (
                  <button type="button" className="flex h-11 w-full items-center gap-2 border-t border-hairline px-3 text-left text-[14px] hover:bg-paper"
                          onClick={() => { setMoreOpen(false); onEditOccasion(); }} data-testid="add-occasion-btn">
                    <Cake size={15} /> Add an occasion
                  </button>
                )}
                {occupied && onMove && (
                  <button type="button" className="flex h-11 w-full items-center gap-2 border-t border-hairline px-3 text-left text-[14px] hover:bg-paper"
                          onClick={() => { setMoreOpen(false); onMove(); }} data-testid="move-table-btn">
                    <ArrowLeftRight size={15} /> Move to another table
                  </button>
                )}
                {occupied && (
                  <button type="button" className="flex h-11 w-full items-center gap-2 px-3 text-left text-[14px] hover:bg-paper"
                          onClick={() => { setMoreOpen(false); onStatusChange('cleaning'); }} data-testid="clear-table-btn">
                    <Eraser size={15} /> Clear table
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </BackBar>
  );
};

export default TableOrderHeader;
